import { Injectable, signal, inject } from '@angular/core';
import { supabase } from '../supabase';
import { ACS, ACSTeam } from '../models';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { OperatorService } from './operator.service';

@Injectable({
    providedIn: 'root'
})
export class AcsService {
    private loadingService = inject(LoadingService);
    private toast = inject(ToastService);
    private operatorService = inject(OperatorService);

    public acsTable = signal<ACS[]>([]);
    public acsTeamsTable = signal<ACSTeam[]>([]);

    async refreshAcs(municipalityId: string | null) {
        if (!municipalityId) return;
        const { data, error } = await supabase
            .from('acs')
            .select('*')
            .eq('municipality_id', municipalityId);
        if (error) {
            console.error('[AcsService] ERRO ACS (possível bloqueio RLS):', error.code, error.message);
            return;
        }
        console.log('[AcsService] Raw ACS data from DB:', data?.map(a => ({ id: a.id, name: a.name, location_id: a.location_id })));
        if (data?.length === 0) {
            console.warn('[AcsService] AVISO: Nenhum ACS retornado para município', municipalityId, '- verifique políticas RLS na tabela acs');
        }
        this.acsTable.set((data || []).map(a => ({
            id: a.id,
            municipalityId: a.municipality_id,
            locationId: a.location_id,
            teamId: a.team_id,
            name: a.name,
            contactNumber: a.contact_number,
            operatorId: a.operator_id,
            login: a.login,
            password: a.password,
            quotas: a.quotas // Manual booking limits map
        })));
    }

    async refreshAcsTeams(municipalityId: string | null) {
        if (!municipalityId) return;
        const { data, error } = await supabase
            .from('acs_teams')
            .select('*')
            .eq('municipality_id', municipalityId);
        if (error) {
            console.error('[AcsService] ERRO ACS_TEAMS (possível bloqueio RLS):', error.code, error.message);
            return;
        }
        this.acsTeamsTable.set((data || []).map(t => ({
            id: t.id,
            municipalityId: t.municipality_id,
            locationId: t.location_id,
            name: t.name
        })));
    }

    async addAcs(acs: Partial<ACS>) {
        const { data, error } = await supabase
            .from('acs')
            .insert([{
                municipality_id: acs.municipalityId,
                location_id: acs.locationId,
                team_id: acs.teamId,
                name: acs.name?.toUpperCase(),
                contact_number: acs.contactNumber ?? ''
            }])
            .select()
            .single();
        if (error) throw error;
        await this.refreshAcs(acs.municipalityId!);
        return data;
    }

    async addAcsWithOperator(acs: Partial<ACS>, login: string, pass: string) {
        let op: any;
        try {
            // 1. Create Operator
            op = await this.operatorService.createOperator(
                acs.name!,
                acs.municipalityId!,
                login,
                pass,
                'ACS',
                Number(acs.locationId) || null  // location_id in operators table is an integer FK
            );
        } catch (err: any) {
            // SELF-HEALING: Se o operador já existe mas falhou no vínculo antes, tentamos recuperá-lo
            if (err.message.toLocaleLowerCase().includes('já existe') || err.message.toLocaleLowerCase().includes('already exists')) {
                const { data: existing } = await supabase.from('operators').select('*').eq('login', login.trim()).maybeSingle();
                if (existing) {
                    console.warn('[AcsService] Operador pré-existente encontrado, tentando vincular...');
                    // Verificamos se já está vinculado a outro ACS
                    const { data: linked } = await supabase.from('acs').select('id').eq('operator_id', existing.id).maybeSingle();
                    if (linked) throw new Error(`O usuário "${login}" já está vinculado a outro agente.`);
                    op = existing;
                } else {
                    throw err;
                }
            } else {
                throw err;
            }
        }

        // 2. Insert into ACS
        const { data, error } = await supabase
            .from('acs')
            .insert([{
                municipality_id: acs.municipalityId,
                location_id: acs.locationId,
                team_id: acs.teamId,
                name: acs.name?.toUpperCase(),
                contact_number: acs.contactNumber ?? '',
                operator_id: op.id
            }])
            .select()
            .single();

        if (error) {
            console.error('[AcsService] Erro ao vincular ACS ao Operador:', error);
            if (error.message.includes('column "operator_id"')) {
                throw new Error('Erro de Banco de Dados: A coluna "operator_id" não foi encontrada na tabela "acs". Por favor, execute o script SQL de atualização.');
            }
            throw error;
        }

        await this.refreshAcs(acs.municipalityId!);
        return data;
    }

    async updateAcsWithOperator(id: string, updates: Partial<ACS>, operatorId?: number, login?: string, pass?: string) {
        // Fetch current ACS record to get existing login/password/municipality_id if needed
        const { data: currentAcs } = await supabase.from('acs').select('*').eq('id', id).maybeSingle();
        const munId = updates.municipalityId || currentAcs?.municipality_id;

        if (login || pass) {
            const effectiveLogin = login || currentAcs?.login || '';
            const effectivePass = pass || currentAcs?.password || '';
            
            // 1. Sincroniza auth.users e tabela acs via RPC
            const { error: rpcError } = await supabase.rpc('secure_upsert_acs', {
                p_id: id,
                p_name: updates.name?.toUpperCase() || currentAcs?.name || '',
                p_login: effectiveLogin,
                p_password: effectivePass,
                p_contact_number: updates.contactNumber ?? currentAcs?.contact_number ?? '',
                p_municipality_id: munId,
                p_location_id: updates.locationId || currentAcs?.location_id || null
            });
            if (rpcError) throw rpcError;

            // Também atualiza campos adicionais não cobertos pelo RPC (team_id, quotas)
            await supabase.from('acs').update({
                team_id: updates.teamId !== undefined ? updates.teamId : currentAcs?.team_id,
                quotas: updates.quotas !== undefined ? updates.quotas : currentAcs?.quotas
            }).eq('id', id);

            // 2. Atualiza tabela operators se vinculado
            if (operatorId) {
                const opUpdates: any = {
                    name: updates.name?.toUpperCase() || currentAcs?.name || ''
                };
                if (login) opUpdates.login = login;
                if (pass) opUpdates.password = pass;

                const { error: opError } = await supabase.from('operators').update(opUpdates).eq('id', operatorId);
                if (opError) throw opError;

                if (pass) {
                    const { data: { session } } = await supabase.auth.getSession();
                    const { data: op } = await supabase.from('operators').select('auth_user_id').eq('id', operatorId).single();
                    if (session?.user && op?.auth_user_id === session.user.id) {
                        await supabase.auth.updateUser({ password: pass });
                    }
                }
            } else if (login && pass && munId) {
                // Cria o operador caso ainda não exista e vincula
                let op: any;
                try {
                    op = await this.operatorService.createOperator(
                        updates.name || currentAcs?.name || 'ACS',
                        munId,
                        login,
                        pass,
                        'ACS',
                        Number(updates.locationId) || null
                    );
                } catch (err: any) {
                    if (err.message.toLocaleLowerCase().includes('já existe') || err.message.toLocaleLowerCase().includes('already exists')) {
                        const { data: existing } = await supabase.from('operators').select('*').eq('login', login.trim()).maybeSingle();
                        if (existing) {
                            const { data: linked } = await supabase.from('acs').select('id').eq('operator_id', existing.id).maybeSingle();
                            if (linked) throw new Error(`O usuário "${login}" já está vinculado a outro agente.`);
                            op = existing;
                        } else throw err;
                    } else throw err;
                }
                await supabase.from('acs').update({ operator_id: op.id }).eq('id', id);
            }
        } else {
            // Apenas dados não-credenciais: UPDATE direto na tabela acs
            const { error: acsError } = await supabase.from('acs').update({
                team_id: updates.teamId,
                name: updates.name?.toUpperCase(),
                contact_number: updates.contactNumber,
                location_id: updates.locationId,
                quotas: updates.quotas
            }).eq('id', id);
            if (acsError) throw acsError;

            if (operatorId && updates.name) {
                await supabase.from('operators').update({ name: updates.name.toUpperCase() }).eq('id', operatorId);
            }
        }

        if (munId) {
            await this.refreshAcs(munId);
            await this.operatorService.refreshOperators(munId);
        }
    }

    async updateAcs(id: string, updates: Partial<ACS>) {
        const { data, error } = await supabase
            .from('acs')
            .update({
                location_id: updates.locationId,
                team_id: updates.teamId,
                name: updates.name?.toUpperCase(),
                contact_number: updates.contactNumber,
                quotas: updates.quotas
            })
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async deleteAcs(id: string) {
        const { data: acsRow } = await supabase.from('acs')
            .select('auth_user_id, operator_id').eq('id', id).maybeSingle();

        if (acsRow?.auth_user_id && !acsRow?.operator_id) {
            // ACS tem usuário auth próprio, limpar via RPC
            const { error } = await supabase.rpc('delete_acs_auth_user', { p_acs_id: id });
            if (error) throw error;
        } else {
            // Apenas deleta o registro da tabela acs
            const { error } = await supabase.from('acs').delete().eq('id', id);
            if (error) throw error;
        }
    }

    async addAcsTeam(team: Partial<ACSTeam>) {
        const { data, error } = await supabase
            .from('acs_teams')
            .insert([{
                municipality_id: team.municipalityId,
                location_id: team.locationId,
                name: team.name?.toUpperCase()
            }])
            .select()
            .single();
        if (error) throw error;
        await this.refreshAcsTeams(team.municipalityId!);
        return data;
    }

    async updateAcsTeam(id: string, updates: Partial<ACSTeam>) {
        const { data, error } = await supabase
            .from('acs_teams')
            .update({
                name: updates.name?.toUpperCase()
            })
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async deleteAcsTeam(id: string) {
        const { error } = await supabase.from('acs_teams').delete().eq('id', id);
        if (error) throw error;
    }
}

